'use client'

import type { AriaAttributes, InputHTMLAttributes } from 'react'
import type { Country } from 'react-phone-number-input'
import { forwardRef } from 'react'
import PhoneInputBase, { isValidPhoneNumber } from 'react-phone-number-input'
import { cn } from '../cn'
import 'react-phone-number-input/style.css'

// Mirrors the shadcn Input primitive (apps share the same Tailwind tokens) so
// the number field matches the rest of the form. The country select + flag is
// rendered to its left by react-phone-number-input.
const INPUT_CLASS
  = 'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20'

const StyledPhoneField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => {
    return <input ref={ref} className={cn(INPUT_CLASS, className)} {...props} />
  },
)

interface Props extends Pick<AriaAttributes, 'aria-invalid'> {
  // E.164 value (e.g. "+5511999999999"), or '' when empty.
  value: string
  onChange: (value: string) => void
  id?: string
  name?: string
  placeholder?: string
  className?: string
  // Pre-selected country for the flag/dial code before the user types.
  defaultCountry?: Country
  disabled?: boolean
  onBlur?: () => void
}

/**
 * International phone field with a country flag selector. Parses/validates with
 * libphonenumber-js (via react-phone-number-input) and emits an E.164 string.
 */
export function PhoneInput({
  value,
  onChange,
  id,
  name,
  placeholder,
  className,
  defaultCountry = 'US',
  disabled,
  onBlur,
  ...rest
}: Props) {
  return (
    <PhoneInputBase
      id={id}
      name={name}
      international
      defaultCountry={defaultCountry}
      value={value || undefined}
      onChange={next => onChange(next ?? '')}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={placeholder}
      inputComponent={StyledPhoneField}
      aria-invalid={rest['aria-invalid']}
      className={cn('flex items-center gap-2', className)}
    />
  )
}

/** True when `value` is a valid international phone number (E.164). */
export function isValidPhone(value: string): boolean {
  return Boolean(value) && isValidPhoneNumber(value)
}
